import * as React from 'react';

import type { HeadProps, PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { TextBlock } from 'stile-shared/src/components/2020/blocks/TextBlock';
import { Link } from 'stile-shared/src/components/2020/common/Link';
import { H2, H3, P, Spacer, Ul } from 'stile-shared/src/components/2020/common/Primitives';
import { Quote } from 'stile-shared/src/components/2020/common/Quote';
import { Column } from 'stile-shared/src/components/2020/layout/Column';
import { CONTACT } from 'stile-shared/src/constants';

import { theme } from 'styles/theme';

import { PageLayout } from 'templates/2020/PageLayout';
import { SEO } from 'templates/2020/SEO';

import { NextPageBlock } from 'components/blocks/NextPageBlock';
import { EscapeRoomForm } from 'components/common/EscapeRoomForm';

export function Head(props: HeadProps) {
  return (
    <SEO
      title="Science Week"
      description="Celebrate National Science Week with Stile’s hands-on immersive escape room!"
      slug={props.location.pathname}
      uniqueToLocale
    />
  );
}

function ScienceWeekPage(props: PageProps) {
  return (
    <PageLayout {...props}>
      <HeroBlock
        title="Stile’s Science Week Escape Room"
        text="Celebrate National Science Week with Stile’s hands-on immersive escape room!"
        image={
          <StaticImage
            loading="eager"
            aspectRatio={2.37}
            src="../../../stile-shared/assets/images/science-week/cover_science_week.png"
            alt="A movie poster-style image showing the title of the escape room 'Species Survival' with a slightly menacing koala and positive reviews of the escape room."
          />
        }
      />

      <TextBlock>
        <P>
          As a customisable core curriculum, we provide all of the resources you need to deliver a
          complete science program. Each year, we release an exciting escape room to celebrate
          National Science Week. For 2024, we have an extra <em>specie</em>-al one for you!
        </P>
        <P>
          Set in real-world scenarios, our escape rooms test your Year 7-10 students’ scientific
          knowledge. In just 45 minutes, your students will collaborate while solving hands-on
          challenges, find hidden codes and pop them into the interactive simulation in Stile! We
          provide accompanying teacher notes and printable cards.
        </P>
        <Spacer />
        <P>Here’s what teachers have had to say about our escape rooms:</P>
        <Quote
          text="We ran the escape room as a lunchtime Science Week activity and the students had a blast!"
          credit="Deanna Pattieson, Balwyn High School"
        />
        <Quote
          text="You’re my hero! I get excited every year to see the fabulous escape rooms that Stile provides."
          credit="Kathy Church, St Joseph’s Catholic College"
        />
        <Quote
          text="The escape room was a raging success and students had a fantastic time."
          credit="Bianca Cheney, St Monica’s College"
        />
      </TextBlock>

      <TextBlock variant="dark" space={theme.space.l}>
        <Column>
          <H2>Escape room: Species Survival</H2>
          <P>
            This escape room matches the{' '}
            <Link openInNewTab variant="inherit" url="https://www.scienceweek.net.au/">
              2024 National Science Week theme of Species Survival — More than just sustainability
            </Link>
            .
          </P>
        </Column>

        <Column>
          <H3>The scenario</H3>

          <P>
            Set within a conservation park, wildlife conservation scientist Marie welcomes your
            students and explains the important work of scientists and conservationists around the
            world in protecting vulnerable species under threat of extinction. Suddenly, Marie is
            interrupted by the sound of an approaching stampede. Cheeky monkeys have gotten into the
            park’s fusebox, and shut down the electricity to the park gates. These vulnerable
            species are on the loose! It’s up to your students to solve the challenges and find the
            hidden codes to restore power to the gates!
          </P>
          <P>
            Your students have 45 minutes to complete eight challenges on the issues threatening the
            survival of different animals and the innovative ways in which scientists are working to
            save these species from extinction.
          </P>
        </Column>

        <Column>
          <H3>Teacher support materials</H3>
          <P>
            With everything we create, we want to provide teachers with everything you need to
            deliver vibrant lessons, in one place.
          </P>
          <P>
            Accompanying the Escape Room lesson is all of the information needed to run it
            including:
          </P>
          <Ul>
            <li>A teacher guide detailing how to set up each activity</li>
            <li>Challenge cards</li>
            <li>Clue cards</li>
            <li>Team name banners highlighting a vulnerable Australian species</li>
            <li>Printable answer templates</li>
            <li>Model answer cards</li>
          </Ul>
        </Column>
        <Column>
          <H3>Hands-on activities</H3>
          <P>
            We believe Stile is the perfect mix of print, practical and digital. We know the
            importance of hands-on activities, that’s why we have{' '}
            <Link
              openInNewTab
              variant="inherit"
              url="https://blog.stileeducation.com/hands-on-science/"
            >
              over a hundred hands-on pracs, engineering challenges and investigations
            </Link>{' '}
            throughout our collection.
          </P>
          <P>
            If you’d like to run the escape room without students using their devices, teachers can
            project the simulation up the front of the class while students fill in the codes using
            pens or pencils on the printable answer templates.
          </P>
        </Column>
      </TextBlock>

      <TextBlock title="Get the escape room at your school">
        <P>
          All schools that use Stile can access our collection of escape rooms in the{' '}
          <Link
            openInNewTab
            url="https://stileapp.com/au/library/publishers/stile/compilations/science-au-v9/5d43df42-c6a4-495f-a33f-721674d3b270/year/supplementary/preview/escape-room-species-survival/XZgL"
          >
            Stile Science Library
          </Link>
          .
        </P>
        <P>
          If you’d like to try Stile at your school and run the Science Week Escape Room, complete
          the form below.
        </P>

        <EscapeRoomForm />

        <P>
          Please don’t hesitate to get in touch if you have any questions on{' '}
          <Link url={CONTACT.au.phone.url}>{CONTACT.au.phone.text}</Link> or email us at{' '}
          <Link url={CONTACT.email.help.url}>{CONTACT.email.help.text}</Link>.
        </P>
      </TextBlock>

      <NextPageBlock path="/what-is-stile/stile-classroom/" />

      <CtaBlock />
    </PageLayout>
  );
}

export default ScienceWeekPage;
